import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { appointmentsApi } from '@/lib/api';

interface UseSmartSyncOptions {
  queryKey: unknown[];
  startDate: string;
  endDate: string;
  professionalId?: string;
  enabled: boolean;
}

interface UseSmartSyncReturn {
  lastSynced: Date | null;
  isSyncing: boolean;
  syncNow: () => void;
}

export function useSmartSync(options: UseSmartSyncOptions): UseSmartSyncReturn {
  const queryClient = useQueryClient();
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const lastChangeRef = useRef<string>(new Date().toISOString());
  const isVisibleRef = useRef(!document.hidden);

  // Track tab visibility
  useEffect(() => {
    const handler = () => {
      isVisibleRef.current = !document.hidden;
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Lightweight change-detection poll
  const { data: syncData, isFetching: isSyncing } = useQuery({
    queryKey: ['appointments-sync', options.startDate, options.endDate, options.professionalId],
    queryFn: async () => {
      const response = await appointmentsApi.lastChange({
        since: lastChangeRef.current,
        startDate: options.startDate,
        endDate: options.endDate,
        professionalId: options.professionalId,
      });
      return response.data as { hasChanges: boolean; lastChange: string; changedCount: number };
    },
    enabled: options.enabled,
    refetchInterval: () => (isVisibleRef.current ? 10_000 : false),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  // When changes detected, invalidate the appointments query
  useEffect(() => {
    if (syncData?.hasChanges) {
      lastChangeRef.current = syncData.lastChange;
      setLastSynced(new Date());
      queryClient.invalidateQueries({ queryKey: options.queryKey });
    } else if (syncData && !syncData.hasChanges) {
      // No changes but poll succeeded — update lastSynced time
      setLastSynced(new Date());
    }
  }, [syncData, queryClient, options.queryKey]);

  const syncNow = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['appointments-sync'] });
    queryClient.invalidateQueries({ queryKey: options.queryKey });
    setLastSynced(new Date());
  }, [queryClient, options.queryKey]);

  return { lastSynced, isSyncing, syncNow };
}
